import { environment } from '$env/environment';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
// import * as Sentry from '@sentry/browser';
import { ToastrService } from 'ngx-toastr';
import { LanguageService } from './language.service';
import { SwalService } from '$core/shell/swal/swal.service';
import { isUserAgreementNotAcceptedError } from '$core/models/user-agreement-not-accepted-error';
import { isMaintenance } from '$core/helpers/is-maintenance';

@Injectable({
  providedIn: 'root',
})
export class ErrorsHandlerService extends ErrorHandler {
  constructor(private injector: Injector) {
    super();
  }

  handleError(error: Error | HttpErrorResponse) {
    const toastr = this.injector.get(ToastrService, null);
    const translateService = this.injector.get(TranslateService, null);

    while ((<any>error).rejection) {
      error = (<any>error).rejection;
    }

    // The user agreement updating modal is the only feedback for this error.
    if (isUserAgreementNotAcceptedError(error)) {
      return;
    }

    if (error instanceof HttpErrorResponse) {
      // Server or connection error happened
      if (!navigator.onLine) {
        // Handle offline error
        if (toastr) {
          toastr.info(translateService.instant('NO_INTERNET'));
        }
      } else {
        // Handle Http Error (error.status === 403, 404...)
        let title = 'AN_ERROR_HAS_OCCURRED';
        let text = 'TRY_AGAIN_LATER';

        switch (error.status) {
          case 413:
            title = 'SEND_ERROR';
            text = 'REDUCE_FILES_SIZE';
            break;

          case 429:
            title = 'TOO_MANY_REQUESTS';
            text = 'TRY_AGAIN_LATER';
            break;
        }

        toastr.error(
          translateService.instant(text),
          translateService.instant(title)
        );
      }
    } else {
      // Handle Client Error (Angular Error, ReferenceError...)
      if (environment.sentry && !window['webpackHotUpdate']) {
        if (error.message && error.message.includes('Loading chunk')) {
          // Sentry.captureMessage(error.message, Sentry.Severity.Info);
        } else {
          // Sentry.captureException(error);
        }
      }
    }

    if (environment.showConsoleErrors) {
      console.error(error);
    }
  }

  public handleErrorCode(res: HttpErrorResponse) {
    if (isUserAgreementNotAcceptedError(res)) {
      return;
    }

    const router = this.injector.get(Router);
    const languageService = this.injector.get(LanguageService);

    if (!isMaintenance(res) && res.status === 503) {
      return;
    }

    router.navigate(
      [languageService.translateRoute(`/error/${res.status || 503}`)],
      { replaceUrl: true, skipLocationChange: true }
    );
  }

  public showErrorModal(error: HttpErrorResponse): void {
    if (isUserAgreementNotAcceptedError(error)) {
      return;
    }

    const res: HttpErrorResponse = error;
    const swal = this.injector.get(SwalService).swal;
    const translateService = this.injector.get(TranslateService);
    swal.icon = 'error';
    swal.title = translateService.instant('AN_ERROR_HAS_OCCURRED');
    if (res.status === 404) {
      swal.text = translateService.instant(res.error.message || 'NOT_FOUND');
      swal.fire();
    } else {
      let text = res.error
        ? res.error.message || res.error.error
        : res.statusText;

      if (res.error.errors && Object.keys(res.error.errors).length) {
        let arr = [];
        for (const [key, value] of Object.entries(res.error.errors)) {
          arr = arr.concat(value);
        }

        if (arr && arr.length) {
          if (arr.length === 1) {
            text = arr[0];
          } else {
            text = `<ul class="list-dotted list-dotted--spaced text-left">
          ${arr.reduce((str, el) => {
            return '<li>' + el + '</li>';
          }, '')}
          </ul>`;
          }
        }
      }

      swal.html = text;
      swal.fire();
    }
  }
}
